import React, { useCallback, useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {
  applicationStore,
  BetStatus,
  ConnectionPopupsWr,
  GameLaunchManager,
  LoadingContainer,
  PlatformMessagesWrapper,
  popupStore,
  PostMessageListenerMessages,
} from '@pascal/pascal-core';
import { observer } from 'mobx-react-lite';
import { betStore } from '@pascal/pascal-core/shared/core/store';
import { AppDefaultValuesEnum, RoutePaths } from './enums';
import { FactoryRouter, SocketConnection } from './core';
import { gameLiveConnection } from './services';
import GameWebMobileContainer from './containers/gameWebMobileContainer';
import { betDataStore, doBetStore, gameStore } from './store';
import { replayFunction } from './helpers/replayFunction';
import { defaultMinOdd } from './enums/gameConstants';
import { animationStore } from './store/animationStore';

let timeoutId;

const App = observer(() => {
  const { betsSectionsData } = betDataStore;
  const components = useMemo(
    () => [
      {
        path: RoutePaths.game,
        element: <GameWebMobileContainer />,
        defaultRedirectComponent: true,
      },
      {
        path: RoutePaths.replay,
        element: <GameWebMobileContainer />,
      },
    ],
    []
  );

  const lostInternetConnection = useCallback(() => {
    popupStore.toggleReconnect(true);
    timeoutId = setTimeout(() => {
      popupStore.toggleReconnect(false);
      popupStore.onConnectionError(true);
      gameLiveConnection.stop();
    }, 5000);
  }, []);

  const reconnectInternet = useCallback(() => {
    if (!popupStore.connectionError) {
      clearTimeout(timeoutId);
      popupStore.toggleReconnect(false);
    }
  }, [popupStore.connectionError]);

  useEffect(() => {
    window.addEventListener('offline', lostInternetConnection);
    window.addEventListener('online', reconnectInternet);
    return () => {
      window.removeEventListener('offline', lostInternetConnection);
      window.removeEventListener('online', reconnectInternet);
    };
  }, [lostInternetConnection, reconnectInternet]);

  useEffect(() => {
    applicationStore.setIsBogLine(true);
    const intervalId = setInterval(() => {
      return gameLiveConnection.sendPing();
    }, 3000);

    return () => {
      intervalId && clearInterval(intervalId);
    };
  }, []);

  const additionalMessagesParams = [
    {
      name: PostMessageListenerMessages.StopAutoSpins,
      callback: () => {
        betDataStore.stopAllAutoBet(false);
      },
    },
    {
      name: PostMessageListenerMessages.UpdateBalance,
      callback: () => {
        setTimeout(() => gameLiveConnection.getBalance(), 500);
      },
    },
  ];
  const repliedCallback = () => {
    const { repliedBetData } = betStore;
    const betsSectionsInitialData = {};
    const allGameTypes = [];

    const {
      amount,
      gameTypeId: currentGameTypeId,
      additionalParams,
      replayData: { gameTypes },
    } = repliedBetData;

    gameTypes.forEach(({ gameTypeId }) => {
      allGameTypes.push(gameTypeId);
      betsSectionsInitialData[gameTypeId] = {
        isAutoBet: false,
        selectedAutoBetCount: null,
        isAutoCashOut: false,
        autoCashOutOdd: additionalParams || defaultMinOdd,
        betAmount: amount,
        freeBetId: null,
        readyForAutoBet: null,
        freeBetAmount: null,
        freeBetCurrentOdd: null,
      };

      if (gameTypeId !== currentGameTypeId) {
        doBetStore.setInitialDefaultDoBetData(gameTypeId);
        animationStore.setToggleGameClosed(true, gameTypeId);
      } else {
        animationStore.setToggleGameClosed(false, gameTypeId);
      }
    });
    gameStore.setAllGameTypes(allGameTypes);
    gameStore.setActiveGameType(currentGameTypeId);
    doBetStore.setInitialDefaultDoBetData(currentGameTypeId, {
      ...repliedBetData,
      betData: additionalParams,
      status: BetStatus.Pending,
    });
    gameStore.setIsBetsClosed(true, currentGameTypeId);
    betDataStore.setInitialBetSectionsData(betsSectionsInitialData);
    if (additionalParams) {
      betDataStore.autoCashOutToggle(currentGameTypeId);
      betDataStore.setAutoCashOutOdd(additionalParams, currentGameTypeId);
    }
  };

  return (
    <BrowserRouter>
      <LoadingContainer
        appDefaultValuesEnum={AppDefaultValuesEnum}
        gameSpecificRepliedCallback={repliedCallback}
      >
        <PlatformMessagesWrapper
          additionalMessagesParams={additionalMessagesParams}
          autoPlayOnAndOffState={Object.values(betsSectionsData).some((item) => item.isAutoBet)}
        >
          <GameLaunchManager
            callbackForRunningGame={replayFunction}
            GameContainer={<FactoryRouter components={components} />}
            SocketConnectionWr={
              <SocketConnection>
                <FactoryRouter components={components} />
              </SocketConnection>
            }
          />
        </PlatformMessagesWrapper>
        <ConnectionPopupsWr />
      </LoadingContainer>
    </BrowserRouter>
  );
});

export default App;
